import { computeDoOrDumpDerived, sortAcquisitions, type DoOrDumpAcquisition, type DoOrDumpVerdict } from "@/lib/do-or-dump-compute";
import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";

const ACQ_LABEL: Record<DoOrDumpAcquisition, string> = {
  social: "Social",
  ads: "Ads",
  "word-of-mouth": "Word of mouth",
  "not-sure": "Not sure yet",
};

const VALID_ACQ = new Set<DoOrDumpAcquisition>(["social", "ads", "word-of-mouth", "not-sure"]);

function parseAcquisitionCsv(raw: string): DoOrDumpAcquisition[] {
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  const mapped = parts.filter((p): p is DoOrDumpAcquisition => VALID_ACQ.has(p as DoOrDumpAcquisition));
  return sortAcquisitions(mapped.length > 0 ? mapped : ["not-sure"]);
}

function badgeClassForVerdict(verdict: DoOrDumpVerdict): string {
  if (verdict === "DO") return "bg-emerald-400 text-emerald-950 ring-emerald-600/30";
  return "bg-rose-500 text-white ring-rose-900/20";
}

function assertVerdict(v: string): DoOrDumpVerdict {
  const t = (v ?? "").trim().toUpperCase();
  if (t === "DO") return "DO";
  if (t === "DUMP") return "DUMP";
  /** Legacy rows stored TEST — fold into Dump. */
  if (t === "TEST") return "DUMP";
  return "DUMP";
}

/** Shape returned from `do_or_dump_funnel_events` (snake_case). */
export type DoOrDumpFunnelRow = {
  email: string;
  product_primary: string;
  product_sub: string;
  category_path: string;
  hours_per_week: number;
  followers: number;
  avg_views: number;
  acquisition: string;
  price: number;
  cost: number;
  orders_per_month: number;
  goal_monthly: number;
  investment_budget_usd?: number | null;
  profit_per_item: number;
  monthly_profit_guess: number;
  momentum: number;
  capacity: number;
  units_for_goal: number | null;
  verdict: string;
  verdict_line: string;
  time_tier: string;
  audience_tier: string;
  acquisition_tier: string;
  why_you?: string | null;
  product_differentiation?: string | null;
  marketing_emails_consent?: boolean | null;
};

export function funnelRowToDashboardPayload(row: DoOrDumpFunnelRow): DoOrDumpDashboardPayload {
  const verdict = assertVerdict(row.verdict);
  const acquisitions = parseAcquisitionCsv(row.acquisition);
  return {
    email: row.email,
    categoryPath: row.category_path?.trim() || `${row.product_primary}`,
    productPrimary: row.product_primary,
    productSub: row.product_sub ?? "",
    productDifferentiation: (row.product_differentiation ?? "").trim(),
    whyYou: (row.why_you ?? "").trim(),
    acquisitionsLabel: acquisitions.map((a) => ACQ_LABEL[a]).join(" and "),
    followers: Number(row.followers),
    avgViews: Number(row.avg_views),
    goalMonthly: row.goal_monthly,
    investmentBudgetUsd: row.investment_budget_usd ?? 0,
    hoursPerWeek: row.hours_per_week,
    price: row.price,
    cost: row.cost,
    ordersPerMonth: Number(row.orders_per_month),
    profitPerItem: row.profit_per_item,
    monthlyProfitGuess: row.monthly_profit_guess,
    momentum: row.momentum,
    capacity: row.capacity,
    unitsForGoal: row.units_for_goal != null ? Number(row.units_for_goal) : null,
    verdict,
    verdictLine: row.verdict_line,
    badgeClass: badgeClassForVerdict(verdict),
    timeTier: row.time_tier,
    audienceTier: row.audience_tier,
    acquisitionTier: row.acquisition_tier,
  };
}

/** Demo payload for unauthenticated visitors or when no row exists yet. */
export function buildPreviewDoOrDumpDashboardPayload(): DoOrDumpDashboardPayload {
  const acquisitions = sortAcquisitions(["social", "ads"]);
  // Inputs tuned so `computeDoOrDumpDerived` lands on **DO** (goal within ~120% of modeled capacity)
  // Demo inputs tuned so the preview lands on **DO** for marketing clarity.
  const d = computeDoOrDumpDerived({
    price: 48,
    cost: 19,
    ordersPerMonth: 95,
    goalMonthly: 3500,
    hoursPerWeek: 15,
    followers: 3800,
    acquisitions,
  });

  return {
    email: "founder@example.com",
    categoryPath: "Beauty → Skincare",
    productPrimary: "beauty",
    productSub: "skincare",
    productDifferentiation:
      "Clinical-inspired formulas at indie prices with domestic fulfillment — ingredient transparency as the hook.",
    whyYou: "Former pharmacy tech; I translate labels without jargon and people trust that voice.",
    acquisitionsLabel: acquisitions.map((a) => ACQ_LABEL[a]).join(" and "),
    followers: 3800,
    avgViews: 4200,
    goalMonthly: 3500,
    investmentBudgetUsd: 2500,
    hoursPerWeek: 15,
    price: 48,
    cost: 19,
    ordersPerMonth: 95,
    profitPerItem: d.profitPerItem,
    monthlyProfitGuess: d.monthlyProfitGuess,
    momentum: d.momentum,
    capacity: d.capacity,
    unitsForGoal: d.unitsForGoal,
    verdict: d.verdict,
    verdictLine: d.verdictLine,
    badgeClass: d.badgeClass,
    timeTier: d.timeTier,
    audienceTier: d.audienceTier,
    acquisitionTier: d.acquisitionTier,
  };
}
